import { useCallback, useEffect, useMemo, useState } from 'react'
import { Download } from 'lucide-react'
import { PageShell } from '@/components/layout/PageShell'
import { ListColumnsMenu } from '@/components/ui/ListColumnsMenu'
import { ListFiltersButton } from '@/components/ui/ListFiltersButton'
import { ListPaginationBar } from '@/components/ui/ListPaginationBar'
import { ListSearchToolbar } from '@/components/ui/ListSearchToolbar'
import {
  applyColumnVisibilityOrder,
  useConfigurableListColumns,
} from '@/hooks/useConfigurableListColumns'
import { useUserRole } from '@/hooks/useUserRole'
import { showApiError } from '@/lib/utils/notifications'
import { ReportDataTable } from './ReportDataTable'
import { ConfiguredReportFilters } from './ConfiguredReportFilters'
import { getGenericReportList } from '../services/reportsService'
import type { GenericReportListResponse } from '../types/api'
import type {
  ConfiguredReportReportRow,
  ConfiguredReportSortDirection,
} from '../types/pages'
import { buildReportExportUrl } from '../utils/reportExportUrls'
import {
  clearGenericReportSession,
  normalizeSessionPatch,
  readGenericReportSession,
  writeGenericReportSession,
} from '../utils/genericReportSessionFilter'
import {
  buildExportParams,
  buildParams,
  countActiveFilters,
  createDefaultParams,
} from '../utils/configuredReportParams'
import { REPORT_EXPORT_BUTTON_CLASS } from '../constants/configuredReportStyles'

import reportStyles from '../styles/reportsTable.module.css'

import type { ConfiguredReportShellProps } from '../types/components'

export function ConfiguredReportShell({ config }: ConfiguredReportShellProps) {
  const { companyId, propertyId } = useUserRole()
  const scopeLockedValues = useMemo(() => {
    const companyKey =
      config.selectFilters?.find((filter) => filter.kind === 'company')?.key ??
      'company_id'
    const propertyKey =
      config.selectFilters?.find((filter) => filter.kind === 'property')?.key ??
      'property_id'
    return {
      ...(companyId != null ? { [companyKey]: String(companyId) } : {}),
      ...(propertyId != null ? { [propertyKey]: String(propertyId) } : {}),
      ...(config.lockedValues ?? {}),
    }
  }, [companyId, config.lockedValues, config.selectFilters, propertyId])
  const defaultParams = useMemo(() => createDefaultParams(config), [config])
  const [params, setParams] = useState<Record<string, unknown>>(() => {
    const session = readGenericReportSession(config.sessionKey)
    return session
      ? {
          ...defaultParams,
          ...normalizeSessionPatch(session, defaultParams),
          ...scopeLockedValues,
        }
      : { ...defaultParams, ...scopeLockedValues }
  })
  const [searchDraft, setSearchDraft] = useState(() => String(params.q ?? ''))
  const [rows, setRows] = useState<ConfiguredReportReportRow[]>([])
  const [response, setResponse] =
    useState<GenericReportListResponse<ConfiguredReportReportRow> | null>(null)
  const [pageOptions, setPageOptions] = useState({
    current: 1,
    last: 1,
    total: 0,
  })
  const [loading, setLoading] = useState(false)
  const [filtersOpen, setFiltersOpen] = useState(false)

  const columnCustomization = config.columnCustomization
  const trailingColumnIds = useMemo(
    () => [...(columnCustomization?.trailingColumnIds ?? [])],
    [columnCustomization?.trailingColumnIds],
  )
  const trailingColumnIdSet = useMemo(
    () => new Set(trailingColumnIds),
    [trailingColumnIds],
  )
  const columnOptions = useMemo(
    () =>
      config.columns
        .filter((column) => !trailingColumnIdSet.has(column.id))
        .map((column) => ({ id: column.id, label: column.label })),
    [config.columns, trailingColumnIdSet],
  )
  const lockedColumnIds = useMemo(
    () => [...(columnCustomization?.lockedColumnIds ?? [])],
    [columnCustomization?.lockedColumnIds],
  )
  const columnsController = useConfigurableListColumns({
    storageKey:
      columnCustomization?.storageKey ??
      `${config.testIdPrefix}-column-visibility-disabled`,
    columnOptions,
    lockedColumnIds,
    persist: Boolean(columnCustomization),
  })
  const visibleColumns = useMemo(() => {
    if (!columnCustomization) return config.columns
    return applyColumnVisibilityOrder(
      config.columns,
      columnsController.tableColumnIds,
      trailingColumnIds,
    )
  }, [
    columnCustomization,
    columnsController.tableColumnIds,
    config.columns,
    trailingColumnIds,
  ])

  useEffect(() => {
    if (Object.keys(scopeLockedValues).length) {
      setParams((prev) => ({ ...prev, ...scopeLockedValues }))
    }
  }, [scopeLockedValues])

  useEffect(() => {
    setSearchDraft(String(params.q ?? ''))
  }, [params.q])

  useEffect(() => {
    if (!config.search) return
    const timer = setTimeout(() => {
      const q = searchDraft.trim()
      if (q !== String(params.q ?? '').trim()) {
        setParams((prev) => ({ ...prev, q, page: 1 }))
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [config.search, params.q, searchDraft])

  const load = useCallback(
    async (next: Record<string, unknown>) => {
      setLoading(true)
      try {
        const res: GenericReportListResponse<ConfiguredReportReportRow> =
          await getGenericReportList(
            config.apiPath,
            buildParams(config, next),
            `Failed to load ${config.title}.`,
          )
        setRows(res.data ?? [])
        setResponse(res)
        setPageOptions({
          current: Number(res.current ?? 1),
          last: Math.max(1, Number(res.last ?? 1)),
          total: Number(res.total ?? 0),
        })
        writeGenericReportSession(config.sessionKey, {
          ...next,
          ...scopeLockedValues,
        })
      } catch (err) {
        showApiError(err)
      } finally {
        setLoading(false)
      }
    },
    [config, scopeLockedValues],
  )

  useEffect(() => {
    void load(params)
  }, [load, params])

  const sort = (orderBy: string) => {
    setParams((prev) => ({
      ...prev,
      orderBy,
      sortBy:
        prev.orderBy === orderBy && prev.sortBy === 'desc' ? 'asc' : 'desc',
      page: 1,
    }))
  }

  const resetFilters = () => {
    setSearchDraft('')
    clearGenericReportSession(config.sessionKey)
    setParams({ ...defaultParams, ...scopeLockedValues })
  }

  const exportUrl = config.exportPath
    ? buildReportExportUrl(config.exportPath, buildExportParams(config, params))
    : null
  const resetParams = { ...defaultParams, ...scopeLockedValues }
  const activeFilterCount = countActiveFilters(config, params, resetParams)
  const columnsMenu = columnCustomization ? (
    <ListColumnsMenu
      columns={columnsController}
      testIdPrefix={config.testIdPrefix}
    />
  ) : null
  const exportAction = exportUrl ? (
    <a
      href={exportUrl}
      className={REPORT_EXPORT_BUTTON_CLASS}
    >
      <Download className="h-[18px] w-[18px]" aria-hidden />
      Export
    </a>
  ) : null
  const toolbarActions =
    columnsMenu || exportAction ? (
      <>
        {exportAction}
        {columnsMenu}
      </>
    ) : undefined

  return (
    <div data-testid={`${config.testIdPrefix}-page`}>
      <PageShell
        title={config.title}
        pageHeaderTestIdRoot={`${config.testIdPrefix}-page-header`}
        contentClassName="!space-y-6 w-full max-w-none min-w-0"
      >
        {config.search ? (
          <ListSearchToolbar
            searchValue={searchDraft}
            searchPlaceholder="Search.."
            onSearchChange={setSearchDraft}
            activeFilterCount={activeFilterCount}
            onOpenFilters={() => setFiltersOpen(true)}
            testIdPrefix={config.testIdPrefix}
            actions={toolbarActions}
          />
        ) : (
          <div className="flex w-full items-center justify-end gap-2 sm:gap-3">
            {exportAction}
            {columnsMenu}
            <ListFiltersButton
              onClick={() => setFiltersOpen(true)}
              activeFilterCount={activeFilterCount}
              testIdPrefix={config.testIdPrefix}
            />
          </div>
        )}

        <ConfiguredReportFilters
          config={config}
          filters={params}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          onApply={(next) =>
            setParams({ ...next, ...scopeLockedValues, page: 1 })
          }
          onReset={resetFilters}
        />

        {config.notice ? (
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {config.notice}
          </div>
        ) : null}

        <div className={reportStyles.tableCard}>
          <ListPaginationBar
            placement="header"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={Number(params.perPage ?? 15)}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix={config.testIdPrefix}
            summaryLayout="angular"
          />

          <ReportDataTable
            rows={rows}
            columns={visibleColumns}
            loading={loading}
            orderBy={String(params.orderBy ?? '')}
            sortBy={
              String(params.sortBy ?? 'asc') as ConfiguredReportSortDirection
            }
            rowKey={(row, index) =>
              config.rowKey
                ? config.rowKey(row, index)
                : `${row.id ?? ''}-${row.applicant_id ?? ''}-${
                    row.property_id ?? ''
                  }-${index}`
            }
            onSort={sort}
            footer={config.footer?.(rows, response)}
            wrapperClassName={config.tableWrapperClassName}
            tableClassName={config.tableClassName}
            testIdPrefix={config.testIdPrefix}
          />

          <ListPaginationBar
            placement="footer"
            currentPage={pageOptions.current}
            lastPage={pageOptions.last}
            totalRecords={pageOptions.total}
            perPage={Number(params.perPage ?? 15)}
            onPageChange={(page) => setParams((p) => ({ ...p, page }))}
            onPerPageChange={(perPage) =>
              setParams((p) => ({ ...p, perPage, page: 1 }))
            }
            testIdPrefix={config.testIdPrefix}
            summaryLayout="angular"
          />
        </div>
      </PageShell>
    </div>
  )
}
